import {
  Box,
  CircularProgress,
  ClickAwayListener,
  Divider,
  Grow,
  ListItemText,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Tooltip,
} from '@mui/material';
import { useTreeItemUtils } from '@mui/x-tree-view/hooks';
import React, { JSX, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../..//app/store.js';
import {
  useDownloadAttachmentMutation,
  useDownloadLibraryZipMutation,
} from '../../../../../app/athenaApi.js';
import {
  HandlersCreateLibraryDirectoryRequest,
  useGetApiLibraryDirectoriesByDirectoryIdQuery,
  usePostApiLibraryDirectoriesMutation,
} from '../../../../../app/athenaApiGen.js';
import { useLibraryTreeContext } from '../LibraryTreeContext';
import {
  setInfoBarDetail,
  setMoveTargetId,
  setPendingCreateItemId,
  setPendingEditItemId,
  toggleModal,
} from '../../../../../app/library/librarySlice.js';
import NoPermissionTooltip from '../../../../../components/Tooltips/NoPermissionTooltip.js';
import { hasDownloadableFile, menuOptions } from '../../../../library/consts.js';
import { streamZipToDisk } from '../../../../../utils/streamZipToDisk.js';

type TActionMenuProps = {
  handleOpen: (state: boolean) => void;
  open: boolean;
  anchorRef: React.RefObject<null>;
  // The kebab's own entity context — passed synchronously by CustomLabel so
  // visibility computes immediately without waiting on the async fetchItem +
  // setSelectedItem chain that fires when the row is also clicked.
  entityType?: string;
  entityId?: string;
};

const ActionMenu = (props: TActionMenuProps) => {
  const dispatch = useDispatch();
  const { selectedItem, allowedActions } = useSelector(
    (state: RootState) => state.library,
  );
  const token = useSelector((state: RootState) => state.user.loginToken);
  const [actions, setActions] = useState<JSX.Element[]>([]);
  const [isPreparingZip, setIsPreparingZip] = useState(false);
  const [download] = useDownloadAttachmentMutation();
  const [downloadLibraryZip] = useDownloadLibraryZipMutation();
  const [postDirectory] = usePostApiLibraryDirectoriesMutation();

  // Parent entityType is used only for attachment Delete visibility (the
  // isItemUnderDirectory / isItemUnderProgram checks in the useEffect below) —
  // NOT for deriving programId on create (see selectedDir below for that).
  // Resolved synchronously from the loaded tree so the gate is correct on the
  // menu's first render.
  const { entityTypesById } = useLibraryTreeContext();
  const parentEntityType = selectedItem?.parentId
    ? entityTypesById.get(selectedItem.parentId)
    : undefined;

  // The directory's own record carries its programId/projectId (program_id is
  // NOT NULL in the schema). Used to build a valid create payload for folders
  // nested at any depth — the tree node only knows its immediate parentId, so
  // deriving programId by walking one level up breaks for sub-folders.
  const { data: selectedDir } = useGetApiLibraryDirectoriesByDirectoryIdQuery(
    { directoryId: selectedItem?.entityId ?? '' },
    {
      skip:
        selectedItem?.entityType !== 'library_directory' ||
        !selectedItem?.entityId,
    },
  );

  const { interactions } = useTreeItemUtils({
    itemId: selectedItem?.id ?? '',
  });

  const isRenamingEnabled = !!allowedActions.find(({ action }) =>
    action?.includes('rename-directory'),
  );

  const isArchiveEnabled = !!allowedActions.find(({ action }) =>
    action?.includes('archive-directory'),
  );
  //if user cannot get a directory, then user cannot download a directory
  const isDownloadAvailable = !!allowedActions.find(({ action }) =>
    action?.includes('get-directory'),
  );

  //if user cannot update a directory, then user cannot upload to a directory
  const isUpdateAvailable = !!allowedActions.find(({ action }) =>
    action?.includes('update-directory'),
  );

  // Utility to download a file from API (octet-stream)
  const handleDownloadFile = async () => {
    if (
      selectedItem?.entityId === undefined ||
      selectedItem?.entityId === null ||
      selectedItem?.entityId === ''
    ) {
      dispatch(setInfoBarDetail('No file ID found for download'));
      return;
    }
    try {
      await download(selectedItem.entityId).unwrap();
    } catch (err) {
      console.error('Download failed:', err);
      dispatch(setInfoBarDetail('Failed to download file'));
    }
  };

  // Download every file under a folder or project as a single ZIP, built
  // server-side (GET /api/library/{id}/download). Offered for folders and
  // projects only — not programs (see the visibility gate below).
  const handleDownloadAll = async () => {
    if (!selectedItem?.entityId) {
      dispatch(setInfoBarDetail('No folder ID found for download'));
      props.handleOpen(false);
      return;
    }
    // Keep the kebab open with an in-item spinner while the ZIP is prepared;
    // the finally closes it once the download resolves.
    setIsPreparingZip(true);
    try {
      // Primary path: stream the archive straight to disk (Chromium), so large
      // folder/project downloads never buffer the whole ZIP in memory and
      // OOM-crash the tab. Same fix as the attachment bulk download (#1615).
      const streamed = await streamZipToDisk({
        url: `/api/library/${selectedItem.entityId}/download`,
        suggestedName: selectedItem.label || 'download',
        token,
      });
      if (!streamed) {
        // Fallback: buffered blob download for browsers without the File
        // System Access API (Firefox/Safari) — fine for small archives.
        await downloadLibraryZip(selectedItem.entityId).unwrap();
      }
    } catch (err) {
      // User dismissed the "Save As" dialog — not an error.
      if ((err as DOMException)?.name !== 'AbortError') {
        console.error('Download all failed:', err);
        dispatch(
          setInfoBarDetail(
            'Failed to download. This folder may have no downloadable files.',
          ),
        );
      }
    } finally {
      setIsPreparingZip(false);
      props.handleOpen(false);
    }
  };

  const handleAddDirectory = async () => {
    if (!selectedItem) return;
    const payload: HandlersCreateLibraryDirectoryRequest = {
      directoryName: 'Untitled',
      programId: '',
    };
    if (selectedItem.entityType === 'program') {
      payload.programId = selectedItem.entityId ?? '';
    } else if (selectedItem.entityType === 'project') {
      // A project's parent in the library tree is always its program.
      payload.programId = selectedItem.parentId ?? '';
      payload.projectId = selectedItem.entityId ?? '';
    } else if (selectedItem.entityType === 'library_directory') {
      // Inherit program/project straight off the selected directory's record
      // so creation works for folders nested at any depth (not just folders
      // sitting directly under a program/project).
      payload.parentDirectoryId = selectedItem.entityId ?? '';
      payload.programId = selectedDir?.programId ?? '';
      if (selectedDir?.projectId) {
        payload.projectId = selectedDir.projectId;
      }
    }
    try {
      const result = await postDirectory({
        handlersCreateLibraryDirectoryRequest: payload,
      }).unwrap();
      // Queue the new folder for auto-rename when its tree item mounts.
      // The tree-node id equals the source-table UUID (see HandlersLibraryTreeNode.id
      // doc: "Original UUID from source table"), which is what the backend returns
      // as directoryId here.
      if (result?.directoryId) {
        dispatch(setPendingEditItemId(result.directoryId));
        // Mark as pending-create so a cancel (Escape / X) on the rename
        // input rolls the creation back instead of leaving an "Untitled"
        // folder behind (issue #1327).
        dispatch(setPendingCreateItemId(result.directoryId));
      }
      // Tree refetch is handled centrally: the create mutation invalidates the
      // 'Library' tag via the enhanceEndpoints override (src/app/athenaApiEnhanced.ts).
    } catch (err) {
      // Surface the backend's validation detail so failures are debuggable
      // from the UI instead of the catch-all string hiding err.data.details.
      const e = err as
        | { data?: { details?: string; message?: string; error?: string } }
        | undefined;
      const detail =
        e?.data?.details ??
        e?.data?.message ??
        e?.data?.error ??
        'Failed to create folder. Please try again.';
      dispatch(setInfoBarDetail(detail));
    }
  };

  const handleCopyUrl = () => {
    // Match the Projects table's Copy URL action — same hash-router URL so
    // the recipient lands on the project on the Projects page. Labeled as
    // "Copy Link" in our kebab for friendlier wording.
    const projectId = selectedItem?.entityId ?? selectedItem?.id;
    if (!projectId) return;
    const projectUrl = `${window.location.origin}/#/projects?projectId=${projectId}`;
    navigator.clipboard
      .writeText(projectUrl)
      .then(() => dispatch(setInfoBarDetail('Link copied to clipboard')))
      .catch(() =>
        dispatch(setInfoBarDetail('Failed to copy link. Please try again.')),
      );
  };

  const handleMenuItemClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    index: number,
  ) => {
    event.stopPropagation();
    const option = menuOptions[index];
    if (option === 'New Folder') {
      handleAddDirectory();
    } else if (option === 'Download') {
      handleDownloadFile();
    } else if (option === 'Download All') {
      // Don't close here — handleDownloadAll keeps the menu open for the
      // spinner and closes it itself.
      handleDownloadAll();
      return;
    } else if (option === 'Delete') {
      dispatch(toggleModal('deleteConfirmation'));
    } else if (option === 'Add File') {
      dispatch(toggleModal('uploader'));
    } else if (option === 'Rename') {
      interactions.toggleItemEditing();
    } else if (option === 'Copy Link') {
      handleCopyUrl();
    } else if (option === 'Move to') {
      // Track the target separately from selectedItem so the right pane
      // doesn't have to flip into a "move mode."
      const id = props.entityId ?? selectedItem?.id;
      if (id) {
        dispatch(setMoveTargetId(id));
        dispatch(toggleModal('movePicker'));
      }
    }

    props.handleOpen(false);
  };

  const handleClose = (event: MouseEvent | TouchEvent) => {
    event.stopPropagation();

    props.handleOpen(false);
  };

  // Content gate for "Download all as zip": recursively walk the selected
  // node's already-loaded subtree. A folder/project with no downloadable file
  // anywhere (e.g. only empty sub-folders) would make the download request
  // fail, so we disable the item and explain why. Deliverables always carry
  // ≥1 attachment, so they naturally pass; programs aren't offered the action.
  const noDownloadableFiles =
    !!selectedItem &&
    (selectedItem.entityType === 'library_directory' ||
      selectedItem.entityType === 'project') &&
    !hasDownloadableFile(selectedItem);
  const downloadTypeWord =
    selectedItem?.entityType === 'project' ? 'project' : 'folder';

  // Whether the selected deliverable can hold attachments — gates "Add Files".
  // Some deliverable types (TAR/PNR/RIP) never do. There's no per-type
  // capability flag in the data model (the backend permits attachments on every
  // form type), so this keys off a hardcoded no-attachment abbreviation list.
  // Fail open: a node without a resolvable abbreviation is treated as capable.
  const deliverableAllowsAttachments =
    selectedItem?.entityType === 'deliverable' &&
    !['tar', 'pnr', 'rip'].includes(
      (selectedItem?.deliverableType?.abbreviation ?? '').toLowerCase(),
    );

  useEffect(() => {
    setActions([]);
    menuOptions.forEach((option: string, index: number) => {
      const isOptionDisabled =
        (option === 'Delete' && !isArchiveEnabled) ||
        (option === 'Download' && !isDownloadAvailable) ||
        (option === 'Download All' && !isDownloadAvailable) ||
        (option === 'Rename' && !isRenamingEnabled) ||
        (option === 'Add File' && !isUpdateAvailable);

      // Use the kebab's own entity context (passed as props by CustomLabel)
      // for the synchronous visibility checks. Falls back to Redux selectedItem
      // for safety when ActionMenu is invoked from a context without props.
      const effectiveEntityType = props.entityType ?? selectedItem?.entityType;
      const isDirectory = effectiveEntityType?.includes('directory');

      const isAttachment = effectiveEntityType?.includes('attachment');

      const isProgram = effectiveEntityType?.includes('program');
      const isProject = effectiveEntityType?.includes('project');
      const isDeliverable = effectiveEntityType === 'deliverable';

      const isItemUnderDirectory = parentEntityType?.includes('directory');
      const isItemUnderProgram = parentEntityType?.includes('program');
      if (
        (option === 'New Folder' && (isProgram || isProject || isDirectory)) ||
        (option === 'Download' && isAttachment) ||
        // Download all as ZIP: folders, projects (a whole project's files), and
        // deliverables that actually have downloadable files. A deliverable is
        // folder-like on the backend, so the same endpoint zips its files; one
        // with no files (e.g. a form-only TAR) is excluded by the file-presence
        // check. Programs are intentionally excluded per product scope.
        (option === 'Download All' &&
          (isDirectory ||
            isProject ||
            (isDeliverable &&
              !!selectedItem &&
              hasDownloadableFile(selectedItem)))) ||
        (option === 'Add File' &&
          (isDirectory ||
            isProgram ||
            isProject ||
            deliverableAllowsAttachments)) ||
        (option === 'Delete' &&
          (isDirectory ||
            isProject ||
            (isAttachment && (isItemUnderDirectory || isItemUnderProgram)))) ||
        (option === 'Rename' && (isDirectory || isAttachment || isProject)) ||
        (option === 'Copy Link' && isProject) ||
        // Move to: folders, deliverables, attachments — uses the existing
        // backend MoveLibraryItem endpoint. Projects/programs intentionally
        // excluded from v1 (more sensitive operation).
        (option === 'Move to' && (isDirectory || isDeliverable || isAttachment))
      ) {
        // Content tooltip only for the Download All item when it's disabled
        // purely because the container has no downloadable files (permission is
        // fine). The permission case is still handled by NoPermissionTooltip,
        // which takes precedence.
        const showNoFilesTooltip =
          option === 'Download All' && noDownloadableFiles && !isOptionDisabled;
        const menuItem = (
          <MenuItem
            key={option}
            disabled={
              isOptionDisabled ||
              (option === 'Download All' &&
                (isPreparingZip || noDownloadableFiles))
            }
            onClick={(event) => handleMenuItemClick(event, index)}
          >
            {option === 'Download All' && isPreparingZip && (
              <CircularProgress size={16} sx={{ mr: 1, color: '#0092CB' }} />
            )}
            <ListItemText>
              {option === 'Delete'
                ? isAttachment
                  ? 'Delete File'
                  : isProject
                    ? 'Delete Project'
                    : 'Delete Folder'
                : option === 'Rename'
                  ? isAttachment
                    ? 'Rename File'
                    : isProject
                      ? 'Rename Project'
                      : 'Rename Folder'
                  : option === 'Download'
                    ? 'Download File'
                    : option === 'Download All'
                      ? isPreparingZip
                        ? 'Preparing…'
                        : 'Download All'
                      : option === 'Add File'
                        ? 'Add Files'
                        : option === 'Move to'
                          ? isAttachment
                            ? 'Move File…'
                            : isDeliverable
                              ? 'Move Deliverable…'
                              : 'Move Folder…'
                          : option}
            </ListItemText>
          </MenuItem>
        );
        const item = (
          <Box key={option}>
            <NoPermissionTooltip isDisabled={isOptionDisabled}>
              {showNoFilesTooltip ? (
                <Tooltip
                  title={`This ${downloadTypeWord} may have no downloadable files.`}
                >
                  <span>{menuItem}</span>
                </Tooltip>
              ) : (
                menuItem
              )}
            </NoPermissionTooltip>
          </Box>
        );
        // Insert a divider before Delete when other actions precede it, so
        // the destructive action sits visually apart from the rest.
        setActions((prev) =>
          option === 'Delete' && prev.length > 0
            ? [...prev, <Divider key="delete-divider" sx={{ my: 0.5 }} />, item]
            : [...prev, item],
        );
      }
    });
  }, [selectedItem?.id, parentEntityType, isPreparingZip]);

  return (
    <Popper
      sx={{ zIndex: 1 }}
      open={props.open}
      anchorEl={props.anchorRef.current}
      role={undefined}
      transition
      disablePortal={false}
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === 'bottom' ? 'center top' : 'center bottom',
          }}
        >
          <Paper>
            <ClickAwayListener onClickAway={handleClose}>
              <MenuList autoFocusItem>
                {actions.length > 0 ? (
                  actions
                ) : (
                  //this will not be the case forever because all items should have one options at the very minimal, which is download
                  <ListItemText>No Options Available</ListItemText>
                )}
              </MenuList>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

export { ActionMenu };
